import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../I18nContext';
import { TreePine, Leaf, Shield, Camera, Satellite, Ruler, CheckCircle, ArrowRight, Globe, Activity } from 'lucide-react';

const FEATURES = [
  { icon: TreePine, color: '#2e7d32', bg: '#e8f5e9', title: 'Species-specific carbon', desc: '16 tree species with IPCC allometric equations (AGB + BGB pools, 4-compartment).' },
  { icon: Camera, color: '#1565c0', bg: '#e3f2fd', title: 'MSH-15 instrument surveys', desc: 'Our 18-camera multi-sensor field instrument measures every tree with survey-grade RTK geolocation.' },
  { icon: Activity, color: '#6a1b9a', bg: '#f3e5f5', title: 'Sentinel-2 NDVI', desc: 'Satellite vegetation index cross-validates every tree location independently.' },
  { icon: Shield, color: '#e65100', bg: '#fff3e0', title: 'Immutable audit chain', desc: 'SHA-256 hash chain on every measurement — tamper-evident and regulator-ready.' },
  { icon: Globe, color: '#00695c', bg: '#e0f2f1', title: 'EUDR & CRCF ready', desc: 'Export due diligence statements and EU Carbon Removal project design documents.' },
  { icon: Ruler, color: '#c62828', bg: '#ffebee', title: 'Monte Carlo uncertainty', desc: '10 000-iteration simulation quantifies 95% confidence bounds on every CO₂ estimate.' },
];

const STATS = [
  { value: '16', label: 'Tree species' },
  { value: '10k', label: 'MC iterations' },
  { value: '4', label: 'Carbon pools' },
  { value: '100%', label: 'IPCC-compliant' },
];

const STEPS = [
  { n: '01', title: 'Register your farm', desc: 'Create a profile, upload land documents, and draw your boundary on the map.' },
  { n: '02', title: 'Trees are measured', desc: 'A trained field agent surveys your plot with the MSH-15 instrument — centimetre GPS, per-tree height, DBH and crown.' },
  { n: '03', title: 'Carbon is calculated', desc: 'Allometric equations + Monte Carlo give you CO₂ with 95% confidence bounds.' },
  { n: '04', title: 'Issue carbon credits', desc: 'Satellite cross-check, audit log, and CRCF PDD — ready for market.' },
];

export default function HomePage() {
  const { t } = useI18n();

  useEffect(() => { document.title = `AvoClima — ${t('nav.home')}`; }, [t]);

  return (
    <div className="bg-bg-page">

      {/* Hero */}
      <div className="min-h-[88vh] flex items-center justify-center relative overflow-hidden px-6 py-20"
        style={{ background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 35%, #1b5e20 65%, #33691e 100%)', backgroundSize: '300% 300%' }}>
        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.07 }} preserveAspectRatio="xMidYMid slice">
          <defs>
            <pattern id="grid" width="48" height="48" patternUnits="userSpaceOnUse">
              <path d="M 48 0 L 0 0 0 48" fill="none" stroke="white" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
        <div className="absolute -top-[10%] -right-[5%] w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(76,175,80,0.18) 0%, transparent 70%)' }} />
        <div className="absolute -bottom-[15%] -left-[8%] w-[400px] h-[400px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(165,214,167,0.12) 0%, transparent 70%)' }} />

        <div className="text-center text-white relative z-10 max-w-[720px] mx-auto">
          <div className="inline-flex items-center gap-2 bg-white/[0.12] border border-white/25 rounded-full px-4 py-1.5 text-[0.8rem] font-semibold mb-7 tracking-wider" style={{ backdropFilter: 'blur(8px)' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-[#a5d6a7] inline-block" />
            Designed for smallholder agroforestry in East Africa
          </div>

          <h1 className="font-extrabold leading-[1.1] tracking-[-2px] mb-6 text-[clamp(2.4rem,6vw,4rem)]">
            Every tree.<br />
            Every tonne.<br />
            <span className="text-[#a5d6a7]">Verified.</span>
          </h1>

          <p className="text-[1.15rem] opacity-[0.82] leading-relaxed max-w-[560px] mx-auto mb-10 font-normal">
            {t('home.missionText')}
          </p>

          <div className="flex gap-3.5 justify-center flex-wrap">
            <Link to="/register" className="bg-white text-primary px-8 py-[15px] rounded-2xl font-bold no-underline text-[0.95rem] inline-flex items-center gap-2 shadow-[0_4px_16px_rgba(0,0,0,0.18)] transition-all">
              <TreePine size={17} />
              Register Your Farm
            </Link>
            <Link to="/dashboard" className="bg-transparent text-white px-8 py-[15px] rounded-2xl font-semibold no-underline text-[0.95rem] inline-flex items-center gap-2 border-[1.5px] border-white/45 transition-all">
              <ArrowRight size={17} />
              View Live Map
            </Link>
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div className="bg-white border-b border-[#e8f5e9] py-7 px-6">
        <div className="max-w-[800px] mx-auto grid grid-cols-4 gap-4">
          {STATS.map((s) => (
            <div key={s.label} className="text-center py-1">
              <div className="text-[2rem] font-extrabold text-primary leading-none mb-1">{s.value}</div>
              <div className="text-[0.78rem] text-text-muted font-medium tracking-wide">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Features grid */}
      <div className="py-20 px-6 bg-bg-page">
        <div className="max-w-[960px] mx-auto">
          <div className="text-center mb-13">
            <div className="inline-flex items-center gap-1.5 bg-bg border border-border rounded-full px-3.5 py-1 text-[0.75rem] font-bold text-primary-light tracking-[0.05em] uppercase mb-4">
              <CheckCircle size={12} /> What we measure
            </div>
            <h2 className="font-extrabold text-text-primary mb-3.5 tracking-tight text-[clamp(1.6rem,4vw,2.4rem)]">
              Designed for how farmers<br />actually work
            </h2>
            <p className="text-text-muted text-[1rem] max-w-[500px] mx-auto leading-relaxed">
              No equipment to buy. No PhD required. Your cooperative's MSH-15 instrument does the measuring — you just enrol and watch your inventory grow.
            </p>
          </div>

          <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
            {FEATURES.map((f) => (
              <div key={f.title} className="card-hover bg-white rounded-[20px] p-7 border border-border-light shadow-[0_1px_4px_rgba(0,0,0,0.05)] cursor-default">
                <div className="w-12 h-12 rounded-[14px] flex items-center justify-center mb-4.5" style={{ background: f.bg }}>
                  <f.icon size={22} style={{ color: f.color }} />
                </div>
                <h3 className="m-0 mb-2 text-[1rem] font-bold text-text-primary">{f.title}</h3>
                <p className="m-0 text-[0.875rem] text-text-muted leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="bg-white py-20 px-6 border-t border-border-light border-b border-border-light">
        <div className="max-w-[960px] mx-auto">
          <div className="text-center mb-13">
            <h2 className="font-extrabold text-text-primary mb-3 tracking-tight text-[clamp(1.6rem,4vw,2.4rem)]">
              {t('home.howItWorks')}
            </h2>
            <p className="text-text-muted text-[1rem] m-0">From registration to carbon credit in four steps.</p>
          </div>

          <div className="grid gap-8" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
            {STEPS.map((s) => (
              <div key={s.n} className="relative">
                <div className="w-12 h-12 rounded-[14px] text-white flex items-center justify-center font-extrabold text-[0.85rem] mb-4 tracking-wide"
                  style={{ background: 'linear-gradient(135deg, #1b5e20, #388e3c)' }}>
                  {s.n}
                </div>
                <h3 className="m-0 mb-2 text-[1rem] font-bold text-text-primary">{s.title}</h3>
                <p className="m-0 text-[0.875rem] text-text-muted leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Science */}
      <div className="py-20 px-6 bg-bg-page">
        <div className="max-w-[680px] mx-auto text-center">
          <div className="inline-flex items-center gap-1.5 bg-bg border border-border rounded-full px-3.5 py-1 text-[0.75rem] font-bold text-primary-light tracking-[0.05em] uppercase mb-4">
            <Leaf size={12} /> The carbon engine
          </div>
          <h2 className="font-extrabold text-text-primary mb-3 tracking-tight text-[clamp(1.6rem,4vw,2.2rem)]">
            IPCC-standard allometrics
          </h2>
          <p className="text-text-muted text-[0.95rem] mb-8 leading-relaxed">
            Species-calibrated equations with Monte Carlo uncertainty propagation over 10 000 iterations.
          </p>
          <div className="bg-white border border-border rounded-[20px] p-8 text-left shadow-[0_2px_12px_rgba(0,0,0,0.06)]"
            style={{ fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace", fontSize: '0.9rem', lineHeight: 2 }}>
            <div><span className="text-text-light">// Above-ground biomass</span></div>
            <div><span className="text-primary font-bold">AGB</span> = <em>a</em> × DBH<sup><em>b</em></sup></div>
            <div className="mt-2"><span className="text-text-light">// Carbon content (IPCC CF = 0.47)</span></div>
            <div><span className="text-primary font-bold">C</span> = (AGB + BGB) × 0.47</div>
            <div className="mt-2"><span className="text-text-light">// CO₂ equivalent</span></div>
            <div><span className="text-primary font-bold">CO₂e</span> = C × 3.67</div>
          </div>
          <p className="text-text-light text-[0.8rem] mt-4 leading-relaxed">
            {t('home.monteCarloNote')}
          </p>
        </div>
      </div>

      {/* CTA strip */}
      <div className="py-[72px] px-6 text-center relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1b5e20 0%, #33691e 100%)' }}>
        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.05 }} preserveAspectRatio="xMidYMid slice">
          <defs>
            <pattern id="dots" width="24" height="24" patternUnits="userSpaceOnUse">
              <circle cx="12" cy="12" r="1.5" fill="white" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#dots)" />
        </svg>
        <div className="relative z-10 max-w-[560px] mx-auto">
          <h2 className="text-white font-extrabold mb-3.5 tracking-tight text-[clamp(1.6rem,4vw,2.4rem)]">
            Ready to start measuring?
          </h2>
          <p className="text-white/75 text-[1rem] mb-9 leading-relaxed">
            Join smallholder farmers across Kenya building verified carbon inventories with AvoClima.
          </p>
          <div className="flex gap-3.5 justify-center flex-wrap">
            <Link to="/register" className="bg-white text-primary px-9 py-[15px] rounded-2xl font-bold no-underline text-[0.95rem] inline-flex items-center gap-2 shadow-[0_4px_16px_rgba(0,0,0,0.18)]">
              <TreePine size={17} /> Get started — it's free
            </Link>
            <Link to="/about" className="bg-transparent text-white px-7 py-[15px] rounded-2xl font-semibold no-underline text-[0.95rem] inline-flex items-center gap-2 border-[1.5px] border-white/40">
              Learn more
            </Link>
          </div>
        </div>
      </div>

    </div>
  );
}
